<template>
  <div class="trade-box">
    <div class="trade-head">
      <strong>Trade</strong>
      <span>{{ selectedCount }} / {{ wallets.length }}</span>
    </div>

    <div v-if="!quick" class="wallet-grid">
      <button
        v-for="w in wallets"
        :key="w.public_key"
        class="wallet-chip"
        type="button"
        :class="{ on: isSelected(w.public_key), locked: quick }"
        :disabled="quick"
        @click="toggleSelect(w.public_key)"
      >
        <span class="dot"></span>
        <span class="name">{{ w.name || shortKey(w.public_key) }}</span>
        <span class="pct">{{ pctFor(w.public_key) }}</span>
      </button>
    </div>

    <div v-if="!quick" class="pct-row">
      <button type="button" :class="{ on: headerPct === 25 }" @click="setHeaderPct(25)">25</button>
      <button type="button" :class="{ on: headerPct === 50 }" @click="setHeaderPct(50)">50</button>
      <button type="button" :class="{ on: headerPct === 100 }" @click="setHeaderPct(100)">100</button>
    </div>

    <div v-if="error" class="trade-error">{{ error }}</div>

    <div class="trade-footer">
      <div class="limits">
        <div class="pill" title="Compute Unit Limit">
          <span>L:</span>
          <b>{{ formatKK(activeCul) }}</b>
          <button v-if="!quick" type="button" @click="bumpCul(-1000000)">−</button>
          <button v-if="!quick" type="button" @click="bumpCul(1000000)">+</button>
        </div>
        <div class="pill" title="Compute Unit Price">
          <span>P:</span>
          <b>{{ formatKK(activeCup) }}</b>
          <button v-if="!quick" type="button" @click="bumpCup(-50000)">−</button>
          <button v-if="!quick" type="button" @click="bumpCup(50000)">+</button>
        </div>
      </div>
      <div class="act-row">
        <button class="act buy" type="button" :disabled="!canTrade || busy" @click="trade('buy')">Buy</button>
        <button class="act sell" type="button" :disabled="!canTrade || busy" @click="trade('sell')">Sell</button>
        <button class="act star" type="button" :disabled="!canTrade || busy" @click="guardedOpen = true">Buy ★</button>
      </div>
    </div>

    <div v-if="guardedOpen" class="modal-backdrop" @click.self="closeGuarded">
      <div class="guard-modal">
        <div class="guard-head">
          <div>
            <strong>Buy ★</strong>
            <span>{{ entity?.symbol || shortKey(entity?.mint) }}</span>
          </div>
          <button type="button" class="icon-btn" @click="closeGuarded">×</button>
        </div>

        <div class="guard-body">
          <label class="custom-buy">
            <span>Buy size</span>
            <input v-model="customBuySol" type="number" min="0" step="0.001" placeholder="wallet default">
          </label>
          <div v-for="row in guardRows" :key="row.pk" class="guard-row" :class="{ 'no-sl': !row.hasSl }">
            <div class="guard-wallet">
              <b>{{ row.name }}</b>
              <small>{{ row.key }}</small>
            </div>
            <span class="buy-sol">{{ row.sol }}</span>
            <span>TP {{ row.tp }}</span>
            <span>SL {{ row.sl }}</span>
            <span>{{ row.hold }}</span>
          </div>
        </div>

        <div v-if="error" class="trade-error modal-error">{{ error }}</div>

        <div class="guard-actions">
          <button type="button" class="act" @click="closeGuarded">Cancel</button>
          <button type="button" class="act star" :disabled="!canTrade || busy" title="Buy with pumpfun exits" @click="guardedBuy">Buy ★</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import { formatSol, shortKey } from '@/components/_shared/format'

const props = defineProps({
  entity: { type: Object, default: null },
  guardNonce: { type: Number, default: 0 },
  quick: { type: Boolean, default: false },
})

const emit = defineEmits(['done', 'submitted', 'failed', 'cancel'])
const store = useStorage()
const busy = ref(false)
const error = ref('')
const guardedOpen = ref(false)
const headerPct = ref(25)
const customBuySol = ref('')

const wallets = computed(() => Object.values(store.wallets || {}))
const tradeSettings = computed(() => store.trade || { cup: 0, cul: 0, owners: {} })
const owners = computed(() => tradeSettings.value?.owners || {})
const activeWalletKey = computed(() => {
  const preferred = String(store.autotrade?.wallet || '').trim()
  if (preferred && wallets.value.some(w => walletKey(w) === preferred)) return preferred
  const ownerKey = Object.keys(owners.value).find(key => wallets.value.some(w => walletKey(w) === key))
  return ownerKey || walletKey(wallets.value[0])
})
const selectedCount = computed(() => activeWalletKey.value ? 1 : 0)
const selectedKeys = computed(() => activeWalletKey.value ? [activeWalletKey.value] : [])
const firstSelected = computed(() => activeWalletKey.value)
const canTrade = computed(() => !!props.entity?.mint && !!activeWalletKey.value)
const activeCup = computed(() => ownerConfig(activeWalletKey.value).cup || tradeSettings.value.cup || 0)
const activeCul = computed(() => ownerConfig(activeWalletKey.value).cul || tradeSettings.value.cul || 0)
const strategyDecision = computed(() => props.entity?.strategy || props.entity?.strategyDecision || null)
const strategyAction = computed(() => String(strategyDecision.value?.action || strategyDecision.value?.decision?.action || '').toLowerCase())
const strategyGuarded = computed(() => {
  if (strategyAction.value !== 'buy') return false
  const decision = strategyDecision.value?.decision || {}
  return decision.guarded === true || decision.guarded === 'true'
})
const strategyMeta = computed(() => {
  const d = strategyDecision.value || {}
  if (!d || strategyAction.value !== 'buy') return {}
  const decision = d.decision || {}
  const runID = Number(d.run_id || d.runID || d.id || 0)
  const signalID = String(d.signal_id || d.signalID || '').trim()
  return {
    source: 'strategy',
    entry_reason: String(d.reason || decision.reason || '').trim() || 'strategy_buy_signal',
    source_id: signalID || String(runID || '').trim(),
    strategy_id: Number(d.strategy_id || d.strategyID || 0),
    strategy_version: Number(d.strategy_version || d.strategyVersion || d.version || 0),
    strategy_run_id: signalID ? 0 : runID,
    strategy_signal_id: signalID,
  }
})
const guardRows = computed(() => selectedKeys.value.map(pk => {
  const cfg = ownerConfig(pk)
  const w = wallets.value.find(row => row.public_key === pk)
  const pct = normalizePct(cfg.pct)
  const customLamports = customBuyLamports()
  const lamports = customLamports > 0 ? customLamports : Math.floor(Math.max(0, Number(w?.lamports || 0)) * pct / 100)
  return {
    pk,
    name: w?.name || shortKey(pk),
    key: shortKey(pk, 5, 5, 16),
    pct,
    sol: formatSol(lamports),
    takeProfitBps: Number(cfg.takeProfitBps || 0),
    stopLossBps: Number(cfg.stopLossBps || 0),
    tp: pctText(cfg.takeProfitBps),
    sl: pctText(cfg.stopLossBps),
    hold: holdText(cfg.maxHoldMs),
    hasSl: Number(cfg.stopLossBps || 0) > 0,
  }
}))

watch(
  () => props.guardNonce,
  () => {
    if (props.guardNonce) guardedOpen.value = true
  },
  { immediate: true }
)

function normalizePct(v) {
  const p = Math.floor(Number(v))
  if (!Number.isFinite(p)) return 25
  return Math.min(100, Math.max(1, p))
}

function isSelected(pk) {
  return String(pk || '').trim() === activeWalletKey.value
}

function ownerConfig(pk) {
  const raw = owners.value[String(pk || '').trim()]
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
  return { pct: normalizePct(raw == null ? headerPct.value : raw), cup: 0, cul: 0, takeProfitBps: 0, stopLossBps: 0, maxHoldMs: 0, enabled: true }
}

function pctFor(pk) {
  return normalizePct(ownerConfig(pk).pct)
}

async function setHeaderPct(p) {
  const v = normalizePct(p)
  headerPct.value = v
  const key = activeWalletKey.value
  if (!key) return
  const next = { ...owners.value }
  next[key] = { ...ownerConfig(key), pct: v }
  await store.updateTrade({ owners: next })
}

async function toggleSelect(pk) {
  if (props.quick) return
  const key = String(pk || '').trim()
  if (!key) return
  const next = { ...owners.value }
  if (next[key] == null) next[key] = { pct: normalizePct(headerPct.value), cup: 0, cul: 0, takeProfitBps: 0, takeProfitLamports: 0, stopLossBps: 0, maxHoldMs: 0, enabled: false }
  await store.updateTrade({ owners: next })
  await store.updateAutotrade({ wallet: key })
}

async function trade(method) {
  if (!canTrade.value || busy.value) return
  if (method === 'buy' && strategyGuarded.value) {
    await guardedBuy()
    return
  }
  busy.value = true
  error.value = ''
  emit('submitted', { kind: method, mint: props.entity.mint })
  try {
    await store.createTradeBatch(props.entity.mint, method, submitOwners(method), method === 'buy' ? strategyMeta.value : {})
    emit('done', { kind: method, mint: props.entity.mint })
  } catch (e) {
    error.value = e?.message || 'Trade failed'
    emit('failed', { kind: method, mint: props.entity.mint, error: error.value })
  } finally {
    busy.value = false
  }
}

async function guardedBuy() {
  if (!canTrade.value || busy.value) return
  busy.value = true
  error.value = ''
  emit('submitted', { kind: 'guarded', mint: props.entity.mint })
  try {
    await store.createGuardedTradeBatch(props.entity.mint, submitOwners('buy'), strategyMeta.value)
    guardedOpen.value = false
    emit('done', { kind: 'guarded', mint: props.entity.mint })
  } catch (e) {
    error.value = e?.message || 'Guarded buy failed'
    emit('failed', { kind: 'guarded', mint: props.entity.mint, error: error.value })
  } finally {
    busy.value = false
  }
}

function submitOwners(method) {
  const key = activeWalletKey.value
  if (!key) return {}
  const activeOwners = { [key]: ownerConfig(key) }
  if (method !== 'buy') return activeOwners
  const lamports = customBuyLamports()
  if (lamports <= 0) return activeOwners
  const next = {}
  for (const pk of Object.keys(activeOwners)) {
    next[pk] = { ...ownerConfig(pk), buyLamports: lamports }
  }
  return next
}

function walletKey(wallet) {
  return String(wallet?.public_key || wallet?.publicKey || wallet?.PublicKey || '').trim()
}

function customBuyLamports() {
  const sol = Number(customBuySol.value || 0)
  if (!Number.isFinite(sol) || sol <= 0) return 0
  return Math.floor(sol * 1_000_000_000)
}

function pctText(bps) {
  const n = Number(bps || 0)
  if (!Number.isFinite(n) || n <= 0) return 'off'
  return `${(n / 100).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`
}

function holdText(ms) {
  const n = Number(ms || 0)
  if (!Number.isFinite(n) || n <= 0) return 'hold off'
  return `${Math.round(n / 1000)}s`
}

function closeGuarded() {
  if (busy.value) return
  guardedOpen.value = false
  error.value = ''
  if (props.quick) emit('cancel')
}

async function bumpCul(delta) {
  const key = activeWalletKey.value
  if (!key) return
  const next = { ...owners.value }
  const cfg = ownerConfig(key)
  next[key] = { ...cfg, cul: Math.max(0, Number(cfg.cul || 0) + Number(delta || 0)) }
  await store.updateTrade({ owners: next })
}

async function bumpCup(delta) {
  const key = activeWalletKey.value
  if (!key) return
  const next = { ...owners.value }
  const cfg = ownerConfig(key)
  next[key] = { ...cfg, cup: Math.max(0, Number(cfg.cup || 0) + Number(delta || 0)) }
  await store.updateTrade({ owners: next })
}

function formatKK(n) {
  n = Number(n) || 0
  if (n >= 1_000_000) return `${Number.isInteger(n / 1_000_000) ? n / 1_000_000 : (n / 1_000_000).toFixed(1)}kk`
  if (n >= 1_000) return `${Number.isInteger(n / 1_000) ? n / 1_000 : (n / 1_000).toFixed(1)}k`
  return String(n)
}
</script>

<style scoped>
.trade-box{
  border:1px solid #242424;
  border-radius:8px;
  background:#101010;
  overflow:hidden;
}
.trade-head{
  padding:9px 10px;
  border-bottom:1px solid #222;
  display:flex;
  justify-content:space-between;
  align-items:center;
}
.trade-head strong{ color:#fff; font-size:13px; }
.trade-head span{ color:#8fa1b5; font-size:12px; }
.wallet-grid,
.modal-wallets{
  padding:8px;
  display:grid;
  grid-template-columns:repeat(auto-fit, minmax(118px, 1fr));
  gap:6px;
}
.wallet-chip{
  min-width:0;
  height:28px;
  border:1px solid #2c2c2c;
  border-radius:7px;
  background:#141414;
  color:#9ca3af;
  display:grid;
  grid-template-columns:8px minmax(0, 1fr) auto;
  align-items:center;
  gap:6px;
  padding:0 7px;
  cursor:pointer;
}
.wallet-chip.on{
  border-color:#2563eb;
  background:#101a31;
  color:#e5e7eb;
}
.wallet-chip.locked{
  cursor:default;
}
.wallet-chip:disabled{
  opacity:1;
}
.dot{ width:7px; height:7px; border-radius:999px; background:#4b5563; }
.wallet-chip.on .dot{ background:#3b82f6; box-shadow:0 0 8px rgba(59,130,246,.75); }
.name{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:11px; }
.pct{ font-size:10px; color:#b6c2d2; }
.pct-row{
  padding:0 8px 8px;
  display:grid;
  grid-template-columns:repeat(3, 1fr);
  gap:6px;
}
.act-row{
  min-width:0;
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:6px;
}
.pct-row button,
.act,
.icon-btn{
  height:28px;
  border-radius:999px;
  border:1px solid #333;
  background:transparent;
  color:#aaa;
  font-size:12px;
  font-weight:700;
  cursor:pointer;
}
.pct-row button.on{ border-color:#4b5563; color:#fff; background:#1f2937; }
.act.buy{ border-color:#14532d; color:#86efac; }
.act.sell{ border-color:#5a2020; color:#fca5a5; }
.act.star{ border-color:#315f42; color:#bbf7d0; background:#0b1710; }
.act{ white-space:nowrap; }
.act:disabled{ opacity:.45; cursor:default; }
.trade-error{
  margin:0 8px 8px;
  padding:8px;
  border:1px solid #5a2020;
  border-radius:7px;
  background:#1b0d0d;
  color:#fca5a5;
  font-size:12px;
}
.trade-footer{
  padding:8px;
  border-top:1px solid #222;
  display:grid;
  grid-template-columns:minmax(0, 1fr) minmax(0, 1fr);
  gap:8px;
  align-items:center;
}
.limits{
  min-width:0;
  display:flex;
  flex-wrap:wrap;
  gap:6px;
}
.pill{
  display:flex;
  align-items:center;
  gap:5px;
  border:1px solid #333;
  border-radius:999px;
  padding:2px 5px;
  font-size:11px;
  min-width:0;
}
.pill span{ color:#9ca3af; }
.pill b{ color:#e5e7eb; min-width:28px; text-align:right; }
.pill button{ border:1px solid #333; background:#141414; color:#aaa; border-radius:999px; cursor:pointer; }
.modal-backdrop{
  position:fixed;
  inset:0;
  z-index:9999;
  display:flex;
  align-items:center;
  justify-content:center;
  background:rgba(0,0,0,.68);
}
.guard-modal{
  width:min(460px, calc(100vw - 28px));
  border:1px solid #252525;
  border-radius:10px;
  background:#0b0b0b;
  color:#e5e7eb;
  overflow:hidden;
}
.guard-head{
  padding:12px 14px;
  border-bottom:1px solid #222;
  background:#171717;
  display:flex;
  justify-content:space-between;
  align-items:center;
}
.guard-head strong,
.guard-head span{ display:block; }
.guard-head span{ margin-top:2px; color:#8fa1b5; font-size:12px; }
.icon-btn{ width:26px; padding:0; }
.guard-body{
  padding:12px 14px 14px;
  display:grid;
  grid-template-columns:1fr;
  gap:6px;
}
.custom-buy{
  min-width:0;
  border:1px solid #252525;
  border-radius:7px;
  background:#101010;
  display:grid;
  grid-template-columns:auto minmax(0, 1fr);
  align-items:center;
  gap:8px;
  padding:7px 10px;
}
.custom-buy span{
  color:#8fa1b5;
  font-size:11px;
  white-space:nowrap;
}
.custom-buy input{
  min-width:0;
  height:28px;
  border:1px solid #2a2a2a;
  border-radius:7px;
  background:#090909;
  color:#e5e7eb;
  padding:0 8px;
}
.guard-row{
  min-width:0;
  min-height:40px;
  border:1px solid #252525;
  border-radius:7px;
  background:#101010;
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto auto auto auto;
  align-items:center;
  gap:10px;
  padding:5px 10px;
}
.guard-wallet{
  min-width:0;
  display:grid;
  gap:1px;
}
.guard-wallet b,
.guard-wallet small{
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.guard-wallet b{
  color:#e5e7eb;
  font-size:12px;
}
.guard-wallet small{
  color:#69758a;
  font-size:10px;
  font-variant-numeric:tabular-nums;
}
.guard-row span{
  color:#9ca3af;
  font-size:11px;
  white-space:nowrap;
}
.buy-sol{
  color:#dbeafe !important;
  font-weight:800;
  font-variant-numeric:tabular-nums;
}
.guard-row.no-sl{
  border-color:#2b3545;
}
.guard-row.no-sl span:nth-of-type(3){
  color:#8fa1b5;
}
.modal-error{ margin:0 14px 10px; }
.guard-actions{
  padding:12px 14px;
  border-top:1px solid #222;
  display:flex;
  justify-content:flex-end;
  gap:8px;
}
.guard-actions .act{
  min-width:88px;
  height:32px;
  padding:0 16px;
  border-radius:8px;
}
@media (max-width: 520px){
  .guard-row{
    grid-template-columns:minmax(0, 1fr) auto auto auto;
    gap:8px;
  }
  .guard-row span:nth-of-type(4){
    display:none;
  }
  .trade-box{
    width:100%;
    border-radius:8px;
  }
  .trade-head{
    padding:7px 9px;
  }
  .wallet-grid{
    display:flex;
    overflow-x:auto;
    padding:7px;
    gap:6px;
  }
  .wallet-chip{
    flex:0 0 128px;
  }
  .pct-row,
  .act-row{
    padding:0 7px 7px;
  }
  .trade-footer .act-row{
    padding:0;
  }
  .trade-footer{
    padding:7px;
    overflow:hidden;
  }
  .limits{
    flex-direction:column;
  }
  .pill{
    min-width:0;
    flex:1 1 0;
  }
}
</style>
